import React, { useState, useRef } from 'react';
import styled from '@emotion/styled';
import { useTranslation } from 'react-i18next';
import { ReactComponent as RightArrowSVG } from '../assets/svgs/right-arrow.svg';
import { ReactComponent as LeftArrowSVG } from '../assets/svgs/left-arrow.svg';
import { useSwipeable } from 'react-swipeable';

const Container = styled.div`
  display: flex;
  flex-direction: column;
`;

const Title = styled.div`
  font-size: 40px;
  color: #385963;
  font-weight: 600;
  margin-bottom: 30px;
  text-align: left;
`;

const CarouselContainer = styled.div`
  position: relative;
  width: 1100px;
  height: 600px;
  margin: 0 auto;

  @media (max-width: 768px) {
    width: 100%;
    height: 60vw;
  }
`;

const CarouselWrapper = styled.div`
  width: 100%;
  height: 100%;
  overflow: hidden;
  border-radius: 12px;
  background-color: white;
`;

const Slide = styled.img`
  width: 100%;
  height: 100%;
  object-fit: contain;
  border-radius: 10px;
  display: block;
  margin: auto;
  user-select: none;
  background-color: white;
`;

const NavButton = styled.button`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  border: none;
  color: #385963;
  font-size: 40px;
  padding: 12px;
  cursor: pointer;
  border-radius: 50%;
  z-index: 2;
  transition: transform 0.2s ease;

  &:hover {
    color: #385963;
    transform: translateY(-50%) scale(1.1);
  }

  &:focus {
    outline: none;
  }
`;

const PrevButton = styled(NavButton)`
  left: -60px;
  background-color: transparent;

  @media (max-width: 768px) {
    display: none;
  }
`;

const NextButton = styled(NavButton)`
  right: -60px;
  background-color: transparent;

  @media (max-width: 768px) {
    display: none;
  }
`;

const Indicators = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 15px;
  gap: 8px;
`;

const Dot = styled.button`
  width: 12px;
  height: 12px;
  border-radius: 50%;
  border: none;
  background: ${({ active }) => (active ? '#385963' : '#ccc')};
  cursor: pointer;
  transition: background 0.3s ease;
  &:focus {
    outline: none;
  }
`;

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background: rgba(0, 0, 0, 0.85);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 9999;
`;

const CloseButton = styled.button`
  position: fixed;
  top: 20px;
  right: 30px;
  background: transparent;
  border: none;
  font-size: 36px;
  color: white;
  cursor: pointer;
  z-index: 10000;
  user-select: none;
  &:focus {
    outline: none;
  }
`;

const ModalImage = styled.img`
  max-width: 90vw;
  max-height: 90vh;
  object-fit: contain;
  border-radius: 12px;
  user-select: none;
  cursor: ${({ zoomed }) => (zoomed ? 'zoom-out' : 'zoom-in')};
  transition: transform 0.3s ease;
  transform-origin: ${({ origin }) => `${origin.x}% ${origin.y}%`};
  transform: ${({ zoomed }) => (zoomed ? 'scale(2)' : 'scale(1)')};
`;

const PhotoGallery = ({ name, photos = [] }) => {
  const { i18n } = useTranslation();
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalImage, setModalImage] = useState(null);
  const [isZoomed, setIsZoomed] = useState(false);
  const [zoomOrigin, setZoomOrigin] = useState({ x: 50, y: 50 });

  const prevSlide = () => setCurrentIndex((prev) => (prev === 0 ? photos.length - 1 : prev - 1));
  const nextSlide = () => setCurrentIndex((prev) => (prev === photos.length - 1 ? 0 : prev + 1));

  const handlers = useSwipeable({
    onSwipedLeft: nextSlide,
    onSwipedRight: prevSlide,
    preventScrollOnSwipe: true,
    trackMouse: true,
  });

  const goToSlide = (index) => setCurrentIndex(index);

  const openModal = (imgSrc) => {
    setModalImage(imgSrc);
    setIsModalOpen(true);
    setIsZoomed(false);
    setZoomOrigin({ x: 50, y: 50 });
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setModalImage(null);
    setIsZoomed(false);
    setZoomOrigin({ x: 50, y: 50 });
  };

  const handleZoomClick = (e) => {
    e.stopPropagation();
    const rect = e.target.getBoundingClientRect();
    const xPercent = ((e.clientX - rect.left) / rect.width) * 100;
    const yPercent = ((e.clientY - rect.top) / rect.height) * 100;
    setZoomOrigin({ x: xPercent, y: yPercent });
    setIsZoomed((prev) => !prev);
  };

  if (!photos.length) {
    return (
      <Container>
        <Title>{i18n.t('photos').toUpperCase()}</Title>
        <p>No photos to display.</p>
      </Container>
    );
  }

  return (
    <Container>
      <CarouselContainer>
        <PrevButton onClick={prevSlide} aria-label="Previous slide">
          <LeftArrowSVG height={50} width={50} />
        </PrevButton>

        <CarouselWrapper {...handlers}>
          <Slide
            src={photos[currentIndex]}
            alt={`${name} photo ${currentIndex + 1}`}
            draggable
            onClick={() => openModal(photos[currentIndex])}
            style={{ cursor: 'pointer' }}
          />
        </CarouselWrapper>

        <NextButton onClick={nextSlide} aria-label="Next slide">
          <RightArrowSVG height={50} width={50} />
        </NextButton>
      </CarouselContainer>

      {isModalOpen && (
        <ModalOverlay onClick={closeModal} aria-modal="true" role="dialog">
          <CloseButton onClick={closeModal} aria-label="Close full screen view">
            &times;
          </CloseButton>
          <ModalImage
            src={modalImage}
            alt="Full screen photo"
            zoomed={isZoomed}
            origin={zoomOrigin}
            onClick={handleZoomClick}
          />
        </ModalOverlay>
      )}

      <Indicators>
        {photos.map((_, idx) => (
          <Dot
            key={idx}
            active={idx === currentIndex}
            onClick={() => goToSlide(idx)}
            aria-label={`Go to slide ${idx + 1}`}
          />
        ))}
      </Indicators>
    </Container>
  );
};

export default PhotoGallery;
